import { toJSON, toCSS } from 'css-convert-json';

/**
 * Validation utility functions
 */
/**
 * Validate email format
 */
function emailIsValid(email) {
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    return emailRegex.test(email);
}
/**
 * Validate phone number format
 */
function phoneNumberIsValid(phoneNumber) {
    const phoneRegex = /^(\+?\d{1,3}[-\s]?)?(\(?\d{2,4}\)?[-\s]?)?(\d{3,4}[-\s]?\d{4}|[*]\d{3,4})$/;
    return phoneRegex.test(phoneNumber);
}
/**
 * Validate full name format
 */
function fullNameIsValid(fullName) {
    const nameRegex = /^([\w]{3,})+\s+([\w\s]{3,})+$/;
    return nameRegex.test(fullName);
}
/**
 * Validate password strength
 */
function passwordIsValid(password) {
    // At least 8 characters with at least one letter and one number
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{8,}$/;
    return passwordRegex.test(password);
}
/**
 * Validate URL format
 */
function isValidUrl(url, config) {
    // Check for hash-only URLs like #contact
    if (config?.allowHashOnly && /^#[\w-]+$/.test(url)) {
        return true;
    }
    // Check for relative paths like /about, ./contact, ../home
    if (config?.allowRelative && /^(\.{0,2}\/[\w\-./]*)$/.test(url)) {
        return true;
    }
    // Check for mailto: links
    if (config?.allowMailto &&
        /^mailto:[\w.+-]+@[\w.-]+\.[a-z]{2,}$/i.test(url)) {
        return true;
    }
    // Check for tel: links
    if (config?.allowTel && /^tel:[+\d\s()-]+$/.test(url)) {
        return true;
    }
    // Check for protocol-relative URLs like //example.com/path
    if (config?.allowProtocolRelative && /^\/\/[\w.-]+/.test(url)) {
        return true;
    }
    try {
        new URL(url);
        const patternParts = [
            '^(https?:\\/\\/)?', // protocol
            '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name and extension
                '((\\d{1,3}\\.){3}\\d{1,3}))', // OR ip (v4) address
            '(\\:\\d+)?',
            '(\\/[-a-z\\d%_.~+{}\\|]*)*', // allow curly braces in the path
        ];
        const pattern = new RegExp(patternParts.join(''), 'i');
        return pattern.test(url);
    }
    catch {
        return false;
    }
}
function isValidUrlWithParams(url, config) {
    if (isValidUrl(url, config)) {
        return true;
    }
    try {
        new URL(url);
        const pattern = new RegExp('^(https?:\\/\\/)?' + // protocol
            '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name and extension
            '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
            '(\\:\\d+)?' +
            '(\\/[-a-z\\d%_.~+{}\\|]*)*' + // allow curly braces in the path
            '(\\?[;&a-z\\d%_.~+=\\-{}\\|()]*)?' + // query string
            '(\\#[-a-z\\d_\\/=?&]*)?$', // fragment locator with forward slash, equals, question mark, and ampersand
        'i');
        return !!pattern.test(url);
    }
    catch (_) {
        return false;
    }
}
/**
 * Validate CSS string
 */
function isValidCss(css) {
    try {
        const jsonCss = toJSON(css);
        const backToCss = toCSS(jsonCss);
        return backToCss.length > 0;
    }
    catch {
        return false;
    }
}
/**
 * Check if JWT token is expired
 */
function isJwtExpired(token) {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Math.floor(Date.now() / 1000);
        return payload.exp < currentTime;
    }
    catch {
        return true;
    }
}
/**
 * Email field validator
 */
function formFieldEmailValidator(email, required = false) {
    if (!email && !required) {
        return { status: 'success' };
    }
    if (!email && required) {
        return { status: 'error', message: 'Email is required' };
    }
    if (!emailIsValid(email)) {
        return { status: 'error', message: 'Please enter a valid email address' };
    }
    return { status: 'success' };
}
/**
 * Phone number field validator
 */
function formFieldPhoneNumberValidator(phoneNumber, required = false) {
    if (!phoneNumber && !required) {
        return { status: 'success' };
    }
    if (!phoneNumber && required) {
        return { status: 'error', message: 'Phone number is required' };
    }
    if (!phoneNumberIsValid(phoneNumber)) {
        return { status: 'error', message: 'Please enter a valid phone number' };
    }
    return { status: 'success' };
}
/**
 * URL field validator
 */
function formFieldUrlWithParamsValidator(url, required = false, config) {
    return formFieldUrlValidator(url, required, { ...config, withParams: true });
}
/**
 * URL field validator
 */
function formFieldUrlValidator(url, required = false, config) {
    if (!url && !required) {
        return { status: 'success' };
    }
    if (!url && required) {
        return { status: 'error', message: 'URL is required' };
    }
    if (config?.withParams) {
        if (!isValidUrlWithParams(url, config)) {
            return { status: 'error', message: 'Please enter a valid URL' };
        }
    }
    else {
        if (!isValidUrl(url, config)) {
            return { status: 'error', message: 'Please enter a valid URL' };
        }
    }
    return { status: 'success' };
}
/**
 * Merge multiple field validators
 */
function mergeFormFieldValidators(mode = 'atLeastOnePassing', ...validatorResults) {
    const errors = validatorResults.filter((result) => result?.status === 'error');
    const successes = validatorResults.filter((result) => !result || result?.status === 'success');
    if (mode === 'allPassing') {
        if (errors.length > 0) {
            return { status: 'error', text: errors.map((e) => e?.text).join(', ') };
        }
        return { status: 'success' };
    }
    if (mode === 'atLeastOnePassing') {
        if (successes.length > 0) {
            return { status: 'success' };
        }
        return { status: 'error', text: errors.map((e) => e?.text).join(', ') };
    }
    return undefined;
}
/**
 * Required field validator
 */
function formFieldRequiredValidator(value) {
    if (!value || value.trim().length === 0) {
        return { status: 'error', message: 'This field is required' };
    }
    return { status: 'success' };
}

export { emailIsValid, formFieldEmailValidator, formFieldPhoneNumberValidator, formFieldRequiredValidator, formFieldUrlValidator, formFieldUrlWithParamsValidator, fullNameIsValid, isJwtExpired, isValidCss, isValidUrl, isValidUrlWithParams, mergeFormFieldValidators, passwordIsValid, phoneNumberIsValid };
//# sourceMappingURL=validation.esm.js.map
